import type { FC, ReactNode } from "react"
import React, { Component, useState } from "react"

import styled from "styled-components"

import type { AirbrakeContext } from "~/contexts"
import IS_DEV from "~/utils/isDev"

import { ReactComponent as FeedbackIcon } from "@svg/feedback.svg"

import Button from "./Button"
import { useOutsideClick } from "./ui"

const ButtonWrapper = styled.div`
  position: fixed;
  bottom: 10px;
  right: 20px;
  border-radius: 50%;
  padding: 10px;
  background: var(--white-menu);
  cursor: pointer;
  z-index: 999999;
  @media print {
    display: none;
  }
  svg {
    width: 30px;
    height: 30px;
    path {
      fill: var(--blue);
    }
  }
`

const ModalWrapper = styled.div`
  position: fixed;
  bottom: 80px;
  z-index: 999999;
  right: 20px;
  color: black;
  width: 300px;
  height: 500px;
  padding: 20px;
  background: var(--white);
  backdrop-filter: blur(10px);
  border-radius: 5px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--blue);
  gap: 20px;
  > form {
    height: calc(100% - 115px);
    textarea {
      width: 100%;
      height: 100%;
      font-family: "Roboto", sans-serif;
      font-size: 16px;
      border: 1px solid #cfcfcf;
      border-radius: 5px;
      padding: 10px;
      resize: none;
      :focus {
        outline: none;
      }
    }
  }
`

const ButtonsContainer = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  background-color: var(--blue-form-footer);
  justify-content: center;
  padding: 20px 0;
  position: absolute;
  left: 0;
  bottom: 0;
  > button {
    border: none;
  }
`

type InnerErrorBoundaryProps = {
  children: ReactNode
  reportSent: boolean
  description: string
}

class InnerErrorBoundary extends Component<InnerErrorBoundaryProps> {
  declare public context: React.ContextType<typeof AirbrakeContext>
  public constructor(public props: InnerErrorBoundaryProps) {
    super(props)
  }

  public componentDidUpdate(): void {
    // Send error to Airbrake
    if (this.props.reportSent && this.context?.airbrake && !IS_DEV) {
      this.context.airbrake.notify({
        params: {
          // localStorage: { ...localStorage },
          info: this.props.description,
        },
      })
    }
  }

  public render(): React.ReactNode {
    const { children } = this.props

    return children as React.ReactNode
  }
}

export const Modal: FC<{
  setShowModal: (show: boolean) => void
  modalRef: React.RefObject<HTMLDivElement>
}> = ({ setShowModal, modalRef }) => {
  const sendReport = () => {
    setReportSent(true)
    setDescription(``)
    setShowModal(false)
  }
  const [reportSent, setReportSent] = useState(false)
  const [description, setDescription] = useState(``)

  return (
    <InnerErrorBoundary reportSent={reportSent} description={description}>
      <ModalWrapper ref={modalRef}>
        <h2>Give Feedback</h2>
        <form>
          <textarea
            autoFocus={true}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe your issue here..."
          />
          <ButtonsContainer
            style={{ backgroundColor: `var(--blue-form-footer)` }}
          >
            <Button variant="default" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <a
              href={`mailto:visionsupport@powur.com?subject=${`feedback`}&body=${description}`}
              onClick={sendReport}
            >
              <Button variant="solid">Send</Button>
            </a>
          </ButtonsContainer>
        </form>
      </ModalWrapper>
    </InnerErrorBoundary>
  )
}

export const FeedbackModal: FC = () => {
  const [showModal, setShowModal] = useState(false)
  const [modalRef, buttonRef] = useOutsideClick<HTMLDivElement>({
    action: () => setShowModal(false),
  })

  return (
    <div>
      <ButtonWrapper
        ref={buttonRef}
        onClick={() => setShowModal((prev) => !prev)}
      >
        <FeedbackIcon />
      </ButtonWrapper>
      {showModal && <Modal setShowModal={setShowModal} modalRef={modalRef} />}
    </div>
  )
}
